import { QueryClient, ResetOptions, QueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for resetting queries in cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type for the queries
 */
export interface ResetTellescopeQueriesArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, resets all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional query filters to narrow down which queries to reset */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, Method>,
      TError,
      QueryDataForMethod<Model, Method>,
      TellescopeQueryKey<Model, Method>
    >,
    "queryKey"
  >;
  /** Optional reset options */
  options?: ResetOptions;
}

/**
 * Resets queries in the query client cache for matching Tellescope queries.
 * This resets the queries to their initial state and refetches them if they are currently being rendered.
 *
 * @param args - The reset queries arguments
 * @returns A promise that resolves when reset is complete
 *
 * @example
 * ```tsx
 * // Reset all queries for a method
 * await resetTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * });
 *
 * // Reset specific query
 * await resetTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * // Reset with filters
 * await resetTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   filters: {
 *     type: "active", // Only reset active queries
 *   },
 * });
 *
 * // Reset with options
 * await resetTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   options: {
 *     throwOnError: true, // Throw on error instead of returning rejected promise
 *   },
 * });
 * ```
 */
export const resetTellescopeQueries = async <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  filters,
  options,
}: ResetTellescopeQueriesArgs<Model, Method, TError>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  return queryClient.resetQueries(
    {
      queryKey,
      ...filters,
    } as QueryFilters<TellescopeQueryKey<Model, Method>>,
    options
  );
};
